import { InlineAlert } from "../../components/BaseComponents";
import { useState } from "react";
import { Button } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import Input from "../../components/BaseComponents/Forms/Input";
import useApi from "../../hooks/useApi";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type CreatePortfolioModalProps = {
    onClose: () => void,
    onCreated: (newId: string) => void
    isOpen?: boolean,
};

function CreatePortfolioModal({onClose, onCreated, isOpen}: CreatePortfolioModalProps) {
    const [label, setLabel] = useState('');
    const api = useApi();
    const [error, setError] = useState('');

    const reset = () => {
        setLabel('');
        setError('');
    };

    // Issue #884: this POST is behind RequireWriteAccess, so it can now reject with
    // READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE (#857 item 6) as well as any generic failure.
    // Previously there was no try/catch at all, so a rejection became an uncaught promise
    // rejection with no visible feedback - reset()/onCreated() were never reachable afterwards
    // (no false success), but the user saw nothing explaining the failure. Catch it, show a
    // friendly message, and keep the typed name so the user doesn't have to retype it.
    const create = async () => {
        setError('');
        try {
            const result = await api.post('/portfolios', {
                name: label,
                _csrf: await getCSRFToken(),
            })
            reset();
            onCreated(result.data.id);
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to create this portfolio. Please try again.'));
        }
    }

    return (
        <CanonicalModal
            open={!!isOpen}
            onClose={() => { reset(); onClose(); }}
            size="small"
            title='Create a new portfolio'
            actions={
                <>
                    <Button onClick={create} additionalClass="mr-5" disabled={!label} type="Primary" text="Create portfolio" />
                    <Button onClick={onClose} type="Outlined" text="Cancel" />
                </>
            }
        >
            <Input
                label="Portfolio name"
                name="portfolioName"
                additionalClass="mt-8"
                value={label}
                onChange={e => setLabel(e.target.value)} type="text"
                placeholder="Portfolio name..."
            />
            { error && (
                <InlineAlert type='error' className='mt-4'>{ error }
                </InlineAlert>
            )}
        </CanonicalModal>
    )
}

export default CreatePortfolioModal;
